import { useEffect, type Dispatch, type MouseEvent, type MutableRefObject, type SetStateAction } from 'react'
import { ChatScopeBadge } from '@/components/layout/chat-scope-badge'
import type { Workspace } from '@/providers/workspace-provider'
import { ChatPaneActiveDocBadge } from './chat-pane-active-doc-badge'
import {
  useActiveDocumentSnapshot,
  type ActiveDocumentContextStore,
} from './chat-pane.active-document-context'
import type { DocumentReference } from './chat-pane.types'
import { composerScopeAfterOpenDocument } from '@/lib/chat-conversation-scope'
import { useWorkspace } from '@/providers/workspace-provider'
import { isSessionSoulStackDisplayTitle, isSessionSoulStackPath } from '@/lib/session-scratch-path'

type ChatPaneComposerContextSlotProps = {
  activeDocumentContext: ActiveDocumentContextStore
  dismissedDocIds: Set<string>
  onDismissActiveDocument: (documentId: string) => void
  onRemoveReference?: (documentId: string, ref?: DocumentReference) => void
  onReferenceOpen?: (ref: DocumentReference, event: MouseEvent) => void
  conversationScope: string[] | null
  onScopeChange: (scope: string[] | null) => void
  setConversationScope: Dispatch<SetStateAction<string[] | null>>
  userExplicitlySetScope: MutableRefObject<boolean>
  workspaces: Workspace[]
  /**
   * When the welcome/top context chip already shows this document id,
   * hide the green active-doc badge in the context row (keep scope + refs).
   */
  suppressActiveDocId?: string | null
  /** `scope` stays in the widget row; `chips` is the current-doc row below. */
  part?: 'chips' | 'scope'
}

export function ChatPaneComposerContextSlot({
  activeDocumentContext,
  dismissedDocIds,
  onDismissActiveDocument,
  onRemoveReference,
  onReferenceOpen,
  conversationScope,
  onScopeChange,
  setConversationScope,
  userExplicitlySetScope,
  workspaces,
  suppressActiveDocId = null,
  part = 'chips',
}: ChatPaneComposerContextSlotProps) {
  const { activeWorkspaceIds } = useWorkspace()
  const activeDocument = useActiveDocumentSnapshot(activeDocumentContext)
  const hideActiveDocBadge =
    (!!suppressActiveDocId &&
      !!activeDocument.id &&
      suppressActiveDocId === activeDocument.id) ||
    isSessionSoulStackDisplayTitle(activeDocument.title) ||
    isSessionSoulStackPath(activeDocument.path)

  useEffect(() => {
    if (part !== 'scope') return
    const wsId = activeDocument.path
      ? workspaces.find((workspace) => {
          if (!workspace.contentRoot) return false
          const normalized = activeDocument.path!.startsWith('/')
            ? activeDocument.path!.slice(1)
            : activeDocument.path!
          return normalized.startsWith(workspace.contentRoot.replace(/\/+$/, ''))
        })?.id
      : null

    const next = composerScopeAfterOpenDocument({
      userSetScope: userExplicitlySetScope.current,
      conversationScope,
      openDocumentWorkspaceId: wsId,
      activeWorkspaceIds,
    })
    if (next) setConversationScope(next)
  }, [activeDocument.path, activeWorkspaceIds, conversationScope, part, setConversationScope, userExplicitlySetScope, workspaces])

  if (part === 'scope') {
    return (
      <ChatScopeBadge
        conversationScope={conversationScope}
        onScopeChange={onScopeChange}
        activeDocumentPath={activeDocument.path}
        triggerClassName="h-8 shrink-0 px-1.5 gap-1 border-border/80 bg-background hover:bg-muted/80 shadow-xs @[320px]/compose:px-2 @[320px]/compose:gap-1.5"
        nameClassName="hidden @[360px]/compose:inline"
      />
    )
  }

  return (
    <ChatPaneActiveDocBadge
      variant="inline"
      activeDocumentId={hideActiveDocBadge ? undefined : activeDocument.id}
      activeDocumentTitle={activeDocument.title}
      activeDocDismissed={!!activeDocument.id && dismissedDocIds.has(activeDocument.id)}
      onDismiss={() => {
        if (activeDocument.id) onDismissActiveDocument(activeDocument.id)
      }}
      references={[]}
      onRemoveReference={onRemoveReference}
      onReferenceOpen={onReferenceOpen}
    />
  )
}
